import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@pella/sash-framework';
import { setRequestCommunicationInCache } from '../../utils';
import { markCommunicationAsRead } from '../../api/production-request-api/index.js';
import { useAppContext } from '../../providers';
import { QUERY_KEYS } from '../../data/constants';
/**
 * Hook containing mutation function for marking all communications on a Production Request as read.
 * The API owns the read receipt and returns the request's full communication history, so the
 * updated `readBy` values are server-owned. The cached request lists are updated on success
 * so the open drawer and the card behind it reflect the receipt without a refetch.
 */
export const useMarkCommunicationRead = () => {
  const { selectedResource, selectedLocation } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    /**
     * mutateFn - Marks every communication on the request as read.
     * @param {Local.RequestWithCommunication} request - The request whose communications were viewed.
     * @returns {Promise<Local.Communication[]>} The full communication history carrying the read receipt.
     */
    mutationFn: async (request) => {
      const read = await markCommunicationAsRead(request.id);

      return read.communications ?? [];
    },
    onSuccess: (communication, request) => {
      if (selectedResource)
        queryClient.setQueryData(
          [QUERY_KEYS.REQUESTS_BY_RESOURCE, selectedResource],
          (/** @type {Pella.ProductionRequestAPI.GetRequestsOutput} */ oldData) =>
            setRequestCommunicationInCache(oldData, request.id, communication),
        );

      if (selectedLocation)
        queryClient.setQueryData(
          [QUERY_KEYS.REQUESTS_BY_LOCATION, selectedLocation],
          (/** @type {Pella.ProductionRequestAPI.GetRequestsOutput} */ oldData) =>
            setRequestCommunicationInCache(oldData, request.id, communication),
        );
    },
    onError: () => toast.error('Error marking communications as read.'),
  });
};
